import { useState, useEffect } from "react";
import { api } from "../../api/client";

export default function SsoConfigForm({ clientId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    enabled: false,
    provider_type: "oidc",
    oidc_client_id: "",
    oidc_client_secret: "",
    oidc_issuer_url: "",
    oidc_scopes: "openid email profile",
  });

  useEffect(() => {
    api
      .get(`/api/admin/clients/${clientId}/sso`)
      .then((res) => {
        const c = res.data;
        setConfig(c);
        setForm({
          enabled: c.enabled,
          provider_type: c.provider_type || "oidc",
          oidc_client_id: c.oidc_client_id || "",
          oidc_client_secret: "",
          oidc_issuer_url: c.oidc_issuer_url || "",
          oidc_scopes: "openid email profile",
        });
      })
      .catch((err) => {
        if (err.response?.status !== 404) setError("Erreur lors du chargement");
      })
      .finally(() => setLoading(false));
  }, [clientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.oidc_client_secret) delete payload.oidc_client_secret;
      const res = await api.put(`/api/admin/clients/${clientId}/sso`, payload);
      setConfig(res.data);
      setForm((prev) => ({ ...prev, oidc_client_secret: "" }));
    } catch (err) {
      setError(
        err.response?.data?.detail || "Erreur lors de la sauvegarde",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Supprimer la configuration SSO pour ce client ?")) return;
    setSaving(true);
    try {
      await api.delete(`/api/admin/clients/${clientId}/sso`);
      setConfig(null);
      setForm({
        enabled: false,
        provider_type: "oidc",
        oidc_client_id: "",
        oidc_client_secret: "",
        oidc_issuer_url: "",
        oidc_scopes: "openid email profile",
      });
    } catch (err) {
      setError(
        err.response?.data?.detail || "Erreur lors de la suppression",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div className="text-sm text-slate-500 py-4">Chargement...</div>;

  return (
    <div className="border border-slate-200 rounded-xl p-6 bg-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800">
          Configuration SSO
        </h3>
        {config && (
          <button
            onClick={handleDelete}
            disabled={saving}
            className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            Supprimer la config
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            className="w-5 h-5 rounded border-slate-300 text-brand-red focus:ring-brand-red"
          />
          <span className="text-sm font-medium text-slate-700">
            Activer le SSO (OpenID Connect)
          </span>
        </label>

        {form.enabled && (
          <>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Issuer URL
              </label>
              <input
                type="url"
                value={form.oidc_issuer_url}
                onChange={(e) =>
                  setForm({ ...form, oidc_issuer_url: e.target.value })
                }
                placeholder="https://login.microsoftonline.com/{tenant}/v2.0"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/45 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Client ID
              </label>
              <input
                type="text"
                value={form.oidc_client_id}
                onChange={(e) =>
                  setForm({ ...form, oidc_client_id: e.target.value })
                }
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/45 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Client Secret
              </label>
              <input
                type="password"
                value={form.oidc_client_secret}
                onChange={(e) =>
                  setForm({ ...form, oidc_client_secret: e.target.value })
                }
                placeholder={config ? "Laisser vide pour conserver" : ""}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/45 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Redirect URI (callback)
              </label>
              <code className="block w-full px-3 py-2 rounded-lg bg-slate-100 text-xs font-mono text-slate-600 break-all">
                {window.location.origin}/api/sso/callback
              </code>
            </div>
          </>
        )}

        {error && (
          <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-brand-red hover:bg-brand-red/90 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50"
        >
          {saving ? "Enregistrement..." : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}
